import { useEffect, useMemo, useRef, useState } from "react";
import { getStorageToken, uploadBlob, blobNameForHash, sha256OfBytes, runStorageCheck } from "../../utils/blobStorage";
import { openStoredSet, renderPageThumbnail, extractPageText } from "../../utils/pdfRender";
import { detectSheetId, disciplineOfNumber } from "../../utils/sheetIdDetect";
import { SHEET_DISCIPLINES, disciplineLabel } from "../../utils/sheetNavigation";
import { sqlRead } from "../../utils/sqlClient";

// Add drawings from a plan SET: upload the PDF once to the private drawings container (in blocks, resumable by hash),
// then pick which pages become drawings in this takeoff. The whole set is never loaded into the database or the browser —
// pages are read from storage as they are shown. Nothing is added to the takeoff until you press Add, and a failed upload
// leaves nothing behind.

const btn = (primary, disabled) => ({ fontSize: 12, fontWeight: 700, padding: "6px 14px", borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, border: `1px solid ${primary ? "#2563eb" : "var(--cs-border)"}`, background: primary ? "#2563eb" : "var(--surface)", color: primary ? "#fff" : "var(--text)" });
const mb = (b) => `${Math.round((b / 1048576) * 10) / 10} MB`;

// thumbnails + title-block reading run one page at a time so a 100-page set never floods the network
let QUEUE = Promise.resolve();
const enqueue = (fn) => { const run = QUEUE.then(fn, fn); QUEUE = run.catch(() => {}); return run; };

function PageCard({ doc, page, meta, added, selected, onToggle, onMeta, onSeen }) {
  const ref = useRef(null);
  const [thumb, setThumb] = useState(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || started.current) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (!entries.some(e => e.isIntersecting) || started.current) return;
      started.current = true; io.disconnect();
      enqueue(async () => {
        try { setThumb(await renderPageThumbnail(doc, page, 170)); } catch { /* the card shows no picture */ }
        try { const { items, page: sz } = await extractPageText(doc, page); onSeen(page, detectSheetId(items, sz)); } catch { onSeen(page, { number: null, title: null, discipline: null, confidence: "none" }); }
      });
    }, { rootMargin: "300px" });
    io.observe(el);
    return () => io.disconnect();
  }, [doc, page]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div ref={ref} style={{ border: `1.5px solid ${selected ? "#2563eb" : "var(--cs-border)"}`, borderRadius: 8, padding: 6, width: 190, background: added ? "rgba(21,128,61,0.07)" : "var(--surface)", opacity: added ? 0.7 : 1 }}>
      <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11, color: "var(--text3)", marginBottom: 4, cursor: added ? "default" : "pointer" }}>
        <input type="checkbox" checked={selected} disabled={added} onChange={() => onToggle(page)} /> Page {page}{added && <b style={{ color: "#15803d" }}> · already added</b>}
      </label>
      <div style={{ height: 120, background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {thumb ? <img src={thumb} alt={`Page ${page}`} style={{ maxWidth: "100%", maxHeight: "100%" }} /> : <span style={{ fontSize: 11, color: "var(--text3)" }}>…</span>}
      </div>
      <input value={meta.number ?? ""} placeholder="Sheet no." aria-label={`Sheet number, page ${page}`} disabled={added}
        onChange={e => onMeta(page, { number: e.target.value, discipline: disciplineOfNumber(e.target.value.toUpperCase()) || meta.discipline })}
        style={{ width: "100%", boxSizing: "border-box", marginTop: 4, fontSize: 11.5, padding: "2px 5px", border: "1px solid var(--cs-border)", borderRadius: 4, borderColor: meta.confidence === "low" ? "#f59e0b" : undefined }} />
      <input value={meta.name ?? ""} placeholder={`Page ${page}`} aria-label={`Name, page ${page}`} disabled={added}
        onChange={e => onMeta(page, { name: e.target.value })}
        style={{ width: "100%", boxSizing: "border-box", marginTop: 3, fontSize: 11.5, padding: "2px 5px", border: "1px solid var(--cs-border)", borderRadius: 4 }} />
    </div>
  );
}

export default function DrawingSetDialog({ existingSheets = [], currentUser = "", isLocked, onAdd, onClose }) {
  const [stage, setStage] = useState("pick");            // pick | working | gallery
  const [phase, setPhase] = useState("");                // what the working stage is doing
  const [progress, setProgress] = useState(null);        // { done, total } while uploading
  const [error, setError] = useState("");
  const [set, setSet] = useState(null);                  // { SetID, BlobName, Sha256, FileName, Bytes }
  const [store, setStore] = useState(null);              // { doc, numPages }
  const [metaByPage, setMetaByPage] = useState({});      // page -> { number, name, discipline, confidence }
  const [selected, setSelected] = useState(() => new Set());
  const [filterText, setFilterText] = useState("");
  const [filterDisc, setFilterDisc] = useState("");
  const [adding, setAdding] = useState(false);
  const [check, setCheck] = useState(null);
  const abort = useRef(null);

  useEffect(() => () => abort.current?.abort(), []);

  const addedPages = useMemo(() => new Set(existingSheets.filter(s => set && s.SetID === set.SetID).map(s => s.ImagePage || 1)), [existingSheets, set]);

  async function start(file) {
    setError("");
    if (!file) return;
    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) { setError("Choose a PDF plan set."); return; }
    setStage("working"); abort.current = new AbortController();
    try {
      setPhase("Signing in to drawing storage…");
      const token = await getStorageToken({ interactive: true });
      if (!token) throw new Error("Could not sign in to drawing storage. Allow the sign-in popup; if it keeps failing, an administrator should run Setup ▸ Integrations ▸ Test drawing storage.");

      setPhase("Reading the file…");
      const hash = await sha256OfBytes(await file.arrayBuffer());
      const blobName = blobNameForHash(hash, ".pdf");

      setPhase("Uploading (a file already stored is not uploaded again)…");
      await uploadBlob(file, blobName, { token, signal: abort.current.signal, onProgress: (done, total) => setProgress({ done, total }) });
      setProgress(null);

      setPhase("Recording the set…");
      const reg = await sqlRead("EXEC sp_RegisterDrawingSet @payload = @payload", { payload: JSON.stringify({ sha256: hash, blob_name: blobName, file_name: file.name.slice(0, 255), content_type: "application/pdf", bytes: file.size, uploaded_by: currentUser }) });
      const row = reg?.Table1?.[0] || reg?.[0];
      if (!row?.SetID) throw new Error("The database did not return the stored set.");

      setPhase("Opening the set…");
      const opened = await openStoredSet(blobName);
      await sqlRead("EXEC sp_RegisterDrawingSet @payload = @payload", { payload: JSON.stringify({ sha256: hash, blob_name: blobName, content_type: "application/pdf", bytes: file.size, page_count: opened.numPages }) }).catch(() => {});
      setSet({ SetID: row.SetID, BlobName: blobName, Sha256: hash, FileName: file.name, Bytes: file.size, PageCount: opened.numPages });
      setStore(opened);
      setStage("gallery");
    } catch (e) {
      setError(e?.message || String(e)); setStage("pick"); setProgress(null);
    }
  }

  const onSeen = (page, d) => setMetaByPage(m => ({ ...m, [page]: { ...(m[page] || {}), number: m[page]?.number ?? d.number ?? "", name: m[page]?.name ?? d.title ?? "", discipline: m[page]?.discipline ?? d.discipline ?? null, confidence: d.confidence } }));
  const onMeta = (page, patch) => setMetaByPage(m => ({ ...m, [page]: { ...(m[page] || {}), ...patch } }));
  const metaOf = (p) => metaByPage[p] || { number: "", name: "", discipline: null, confidence: "none" };

  const pages = useMemo(() => {
    if (!store) return [];
    const q = filterText.trim().toLowerCase();
    return Array.from({ length: store.numPages }, (_, i) => i + 1).filter(p => {
      const m = metaByPage[p] || {};
      if (filterDisc && m.discipline !== filterDisc) return false;
      return !q || `${p} ${m.number || ""} ${m.name || ""}`.toLowerCase().includes(q);
    });
  }, [store, metaByPage, filterText, filterDisc]);

  const toggle = (p) => setSelected(prev => { const n = new Set(prev); n.has(p) ? n.delete(p) : n.add(p); return n; });
  const selectable = pages.filter(p => !addedPages.has(p));
  const picked = [...selected].filter(p => !addedPages.has(p)).sort((a, b) => a - b);

  async function add() {
    setAdding(true); setError("");
    try {
      await onAdd({ set, pages: picked.map(p => { const m = metaOf(p); return { page: p, name: (m.name || "").trim() || (m.number ? m.number : `Page ${p}`), number: (m.number || "").trim() || null, discipline: m.discipline || null }; }) });
      onClose();
    } catch (e) { setError(e?.message || String(e)); setAdding(false); }
  }

  async function runCheck() { setCheck("running"); try { setCheck(await runStorageCheck()); } catch (e) { setCheck([{ key: "x", ok: false, label: "The check could not run", detail: e.message }]); } }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 950, background: "rgba(15,23,42,0.5)", display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: "4vh" }}>
      <div role="dialog" aria-label="Add drawings from a plan set" style={{ width: "min(1000px, 96vw)", maxHeight: "92vh", display: "flex", flexDirection: "column", background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 10, boxShadow: "0 16px 48px rgba(0,0,0,0.4)", padding: "14px 18px", fontSize: 13 }}>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 2 }}>Add drawings from a plan set</div>

        {stage === "pick" && (
          <div>
            <div style={{ color: "var(--text3)", fontSize: 12, marginBottom: 10 }}>Choose the PDF set. It is uploaded once to private drawing storage, then you tick only the pages you want to take off.</div>
            {isLocked && <div role="alert" style={{ color: "#dc2626", fontWeight: 700, marginBottom: 8 }}>This revision is locked — drawings cannot be added.</div>}
            <input type="file" accept="application/pdf,.pdf" disabled={isLocked} onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; start(f); }} aria-label="Plan set PDF" data-testid="set-file-input" />
            {error && <div role="alert" style={{ color: "#dc2626", fontWeight: 700, marginTop: 10 }}>{error}</div>}
            <div style={{ marginTop: 14 }}>
              <button type="button" onClick={runCheck} style={btn(false, check === "running")} disabled={check === "running"}>Test drawing storage</button>
              {Array.isArray(check) && (
                <div style={{ marginTop: 8, fontSize: 12.5 }}>
                  {check.map(s => <div key={s.key}><b style={{ color: s.ok ? "#15803d" : "#dc2626" }}>{s.ok ? "✓" : "✗"}</b> {s.label}{!s.ok && s.detail && <div style={{ marginLeft: 16, color: "#b91c1c" }}>{s.detail}</div>}</div>)}
                </div>
              )}
            </div>
          </div>
        )}

        {stage === "working" && (
          <div role="status" style={{ padding: "18px 0" }}>
            <div style={{ fontWeight: 700 }}>{phase}</div>
            {progress && (
              <div style={{ marginTop: 8 }}>
                <div style={{ height: 8, background: "var(--cs-border)", borderRadius: 4, overflow: "hidden" }}><div style={{ width: `${Math.round((progress.done / progress.total) * 100)}%`, height: "100%", background: "#2563eb" }} /></div>
                <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 4 }}>{mb(progress.done)} of {mb(progress.total)}</div>
              </div>
            )}
            <button type="button" onClick={() => { abort.current?.abort(); }} style={{ ...btn(false, false), marginTop: 12 }}>Cancel</button>
          </div>
        )}

        {stage === "gallery" && store && (
          <>
            <div style={{ color: "var(--text3)", fontSize: 12, marginBottom: 8 }}>{set.FileName} · {store.numPages} pages · {mb(set.Bytes)} · stored. Names and numbers are read from each page's title block — check them and fix anything that is wrong.</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
              <input value={filterText} onChange={e => setFilterText(e.target.value)} placeholder="Filter by number or title" aria-label="Filter pages" style={{ fontSize: 12, padding: "4px 8px", border: "1px solid var(--cs-border)", borderRadius: 6, width: 220 }} />
              <select value={filterDisc} onChange={e => setFilterDisc(e.target.value)} aria-label="Filter by discipline" style={{ fontSize: 12, padding: "4px 6px" }}>
                <option value="">All disciplines</option>
                {SHEET_DISCIPLINES.map(d => <option key={d.key} value={d.key}>{disciplineLabel(d.key)}</option>)}
              </select>
              <button type="button" onClick={() => setSelected(prev => new Set([...prev, ...selectable]))} style={btn(false, !selectable.length)} disabled={!selectable.length}>Select shown ({selectable.length})</button>
              <button type="button" onClick={() => setSelected(new Set())} style={btn(false, !selected.size)} disabled={!selected.size}>Clear</button>
            </div>
            <div style={{ overflowY: "auto", flex: 1, display: "flex", flexWrap: "wrap", gap: 8, alignContent: "flex-start", paddingBottom: 6 }}>
              {pages.map(p => <PageCard key={p} doc={store.doc} page={p} meta={metaOf(p)} added={addedPages.has(p)} selected={selected.has(p)} onToggle={toggle} onMeta={onMeta} onSeen={onSeen} />)}
              {!pages.length && <div style={{ color: "var(--text3)", padding: 12 }}>No pages match.</div>}
            </div>
            {error && <div role="alert" style={{ color: "#dc2626", fontWeight: 700, marginTop: 8 }}>{error}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10, alignItems: "center" }}>
              <span style={{ fontSize: 12, color: "var(--text3)", marginRight: "auto" }}>{picked.length} page{picked.length === 1 ? "" : "s"} selected</span>
              <button type="button" onClick={onClose} disabled={adding} style={btn(false, adding)}>Close</button>
              <button type="button" onClick={add} disabled={adding || isLocked || !picked.length} style={btn(true, adding || isLocked || !picked.length)}>{adding ? "Adding…" : `Add ${picked.length || ""} drawing${picked.length === 1 ? "" : "s"}`}</button>
            </div>
          </>
        )}

        {stage !== "gallery" && <div style={{ textAlign: "right", marginTop: 12 }}><button type="button" onClick={onClose} style={btn(false, false)}>Close</button></div>}
      </div>
    </div>
  );
}
